import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import LocationAutocomplete from "../components/LocationAutocomplete";
import BetaFeaturesPanel from "../components/BetaFeaturesPanel";

function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [targetInputs, setTargetInputs] = useState({ target_industries: "", target_titles: "", target_locations: "" });
  const [targetOptions, setTargetOptions] = useState({ industries: [], seniorities: [] });
  const [subscription, setSubscription] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // API Settings (GHL only - email discovery is admin-managed)
  const [apiSettings, setApiSettings] = useState(null);
  const [ghlAccessToken, setGhlAccessToken] = useState("");
  const [ghlLocationId, setGhlLocationId] = useState("");
  const [savingApi, setSavingApi] = useState(false);
  const [testingApi, setTestingApi] = useState(false);
  const [testResults, setTestResults] = useState(null);

  // CRM Integration (simplified - manual API key entry only)
  const [ghlIntegration, setGhlIntegration] = useState(null);

  // Change Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete Account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const daysOfWeek = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" }
  ];

  useEffect(() => {
    loadSettings();
    loadApiSettings();
    loadSubscription();
    loadTargetOptions();
  }, []);

  const loadTargetOptions = async () => {
    try {
      const res = await axios.get(`${API}/lead-targets/options`);
      setTargetOptions({
        industries: res.data.industries || [],
        seniorities: res.data.seniorities || [],
      });
    } catch (error) {
      console.error("Error loading target options:", error);
    }
  };

  const seniorityLabel = (val) => {
    const s = (targetOptions.seniorities || []).find((x) => x.value === val);
    return s ? s.label : val;
  };

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Error loading subscription:", error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings");
      setLoading(false);
    }
  };

  const loadApiSettings = async () => {
    try {
      const response = await axios.get(`${API}/api-settings`);
      setApiSettings(response.data);
    } catch (error) {
      console.error("Error loading API settings:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        enabled: settings.enabled,
        scan_frequency: settings.scan_frequency || 'weekly',
        schedule_day: settings.schedule_day,
        schedule_hour: settings.schedule_hour,
        schedule_minute: settings.schedule_minute,
        days_lookback: settings.days_lookback,
        default_tags: settings.default_tags || [],
        target_industries: settings.target_industries || [],
        target_titles: settings.target_titles || [],
        target_locations: settings.target_locations || []
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    if (settings) {
      setSettings({ ...settings, [key]: value });
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (!t || !settings) return;
    const existing = settings.default_tags || [];
    if (existing.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setNewTag("");
      return;
    }
    setSettings({ ...settings, default_tags: [...existing, t] });
    setNewTag("");
  };

  const removeTag = (tag) => {
    if (!settings) return;
    setSettings({ ...settings, default_tags: (settings.default_tags || []).filter((x) => x !== tag) });
  };

  const addToList = (field, value) => {
    const v = (value || "").trim();
    if (!v || !settings) return;
    const existing = settings[field] || [];
    if (existing.some((x) => x.toLowerCase() === v.toLowerCase())) return;
    setSettings({ ...settings, [field]: [...existing, v] });
  };

  const removeFromList = (field, value) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: (settings[field] || []).filter((x) => x !== value) });
  };

  const handleSaveApiSettings = async () => {
    setSavingApi(true);
    try {
      const payload = {};
      if (ghlAccessToken.trim()) payload.ghl_access_token = ghlAccessToken.trim();
      if (ghlLocationId.trim()) payload.ghl_location_id = ghlLocationId.trim();

      await axios.put(`${API}/api-settings`, payload);
      toast.success("API settings saved successfully!");
      
      // Clear input fields
      setGhlAccessToken("");
      setGhlLocationId("");
      
      // Reload to show masked values
      await loadApiSettings();
    } catch (error) {
      console.error("Error saving API settings:", error);
      toast.error("Failed to save API settings");
    } finally {
      setSavingApi(false);
    }
  };

  const handleTestApiConnections = async () => {
    setTestingApi(true);
    setTestResults(null);
    try {
      const response = await axios.post(`${API}/api-settings/test`);
      setTestResults(response.data);
      
      const allSuccess = Object.values(response.data).every(r => r.status === "success");
      if (allSuccess) {
        toast.success("All API connections successful!");
      } else {
        toast.warning("Some API connections failed. Check results below.");
      }
    } catch (error) {
      console.error("Error testing API connections:", error);
      toast.error("Failed to test API connections");
    } finally {
      setTestingApi(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success("Password changed successfully!");
      
      // Clear fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`);
      toast.success("Account deleted successfully");
      setShowDeleteModal(false);
      logout();
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };


  if (loading || !settings) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-description">
            Configure automated scan schedule and preferences
          </p>
        </div>
        {/* Plan Indicator */}
        <div
          onClick={() => navigate('/plans')}
          data-testid="plan-indicator"
          style={{
            cursor: 'pointer',
            background: subscription?.plan === 'free' ? '#f1f5f9' : 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
            border: subscription?.plan === 'free' ? '1px solid #e2e8f0' : '1px solid #86efac',
            borderRadius: '12px',
            padding: '12px 20px',
            textAlign: 'right',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>Current Plan</div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: subscription?.plan === 'free' ? '#334155' : '#16a34a' 
          }}>
            {subscription?.plan_name || 'Free'}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            {subscription?.leads_remaining || 0} leads left · Click to upgrade
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">Scan Schedule</h2>
        </div>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              checked={settings?.enabled || false}
              onChange={(e) => updateSetting('enabled', e.target.checked)}
              style={{ marginRight: '8px' }}
              data-testid="schedule-enabled-checkbox"
            />
            Enable Automated Scans
          </label>
          <p className="form-help">
            When enabled, scans will run automatically according to the schedule below
          </p>
        </div>

        <div className="form-group" style={{ marginTop: '24px' }}>
          <label className="form-label">Scan Frequency</label>
          <select
            className="form-select"
            value={settings.scan_frequency || 'weekly'}
            onChange={(e) => updateSetting('scan_frequency', e.target.value)}
            disabled={!settings.enabled}
            data-testid="scan-frequency-select"
            style={{ maxWidth: '300px' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly (Every 2 Weeks)</option>
          </select>
          <p className="form-help">
            How often should automated scans run?
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '24px' }}>
          <div className="form-group">
            <label className="form-label">Day of Week</label>
            <select
              className="form-select"
              value={settings.schedule_day}
              onChange={(e) => updateSetting('schedule_day', parseInt(e.target.value))}
              disabled={!settings.enabled || settings.scan_frequency === 'daily'}
              data-testid="schedule-day-select"
            >
              {daysOfWeek.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
            {settings.scan_frequency === 'daily' && (
              <p className="form-help" style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                Not applicable for daily scans
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Hour (0-23)</label>
            <input
              type="number"
              className="form-input"
              value={settings.schedule_hour}
              onChange={(e) => updateSetting('schedule_hour', parseInt(e.target.value))}
              min="0"
              max="23"
              disabled={!settings.enabled}
              data-testid="schedule-hour-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Minute (0-59)</label>
            <input
              type="number"
              className="form-input"
              value={settings.schedule_minute}
              onChange={(e) => updateSetting('schedule_minute', parseInt(e.target.value))}
              min="0"
              max="59"
              disabled={!settings.enabled}
              data-testid="schedule-minute-input"
            />
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '24px' }}>
          <label className="form-label">Days Lookback</label>
          <input
            type="number"
            className="form-input"
            value={settings.days_lookback}
            onChange={(e) => updateSetting('days_lookback', parseInt(e.target.value))}
            min="1"
            max="90"
            style={{ maxWidth: '200px' }}
            data-testid="days-lookback-input"
          />
          <p className="form-help">
            Number of days to look back for new business formations (default: 14)
          </p>
        </div>

        <div style={{ marginTop: '32px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: '#334155' }}>
            Current Schedule
          </h3>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            {settings.enabled ? (
              <>
                {settings.scan_frequency === 'daily' ? (
                  <>
                    Scans will run <strong>daily</strong> at{' '}
                    <strong>{String(settings.schedule_hour).padStart(2, '0')}:{String(settings.schedule_minute).padStart(2, '0')}</strong>{' '}
                    ({settings.schedule_timezone})
                  </>
                ) : settings.scan_frequency === 'biweekly' ? (
                  <>
                    Scans will run <strong>bi-weekly</strong> on <strong>{daysOfWeek.find(d => d.value === settings.schedule_day)?.label}</strong> at{' '}
                    <strong>{String(settings.schedule_hour).padStart(2, '0')}:{String(settings.schedule_minute).padStart(2, '0')}</strong>{' '}
                    ({settings.schedule_timezone})
                  </>
                ) : (
                  <>
                    Scans will run <strong>weekly</strong> on <strong>{daysOfWeek.find(d => d.value === settings.schedule_day)?.label}</strong> at{' '}
                    <strong>{String(settings.schedule_hour).padStart(2, '0')}:{String(settings.schedule_minute).padStart(2, '0')}</strong>{' '}
                    ({settings.schedule_timezone})
                  </>
                )}
              </>
            ) : (
              "Automated scans are currently disabled"
            )}
          </p>
        </div>

        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px', color: '#334155' }}>
            Ideal Customer Profile <span style={{ color: '#7c3aed' }}>· Lead Scoring</span>
          </h3>
          <p className="form-help" style={{ marginBottom: '16px' }}>
            Tell us who your best customers are. We'll automatically score every lead <strong>Hot / Warm / Cold</strong> based on how well it matches these targets (plus contact availability and seniority).
          </p>
          {/* Chip lists shared renderer */}
          {(() => {
            const chipList = (field, chipBg, chipColor, displayFn) => (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '8px 0' }} data-testid={`${field}-list`}>
                {(settings[field] || []).length === 0 ? (
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>None set yet.</span>
                ) : (
                  (settings[field] || []).map((item) => (
                    <span
                      key={item}
                      data-testid={`${field}-item-${item}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: chipBg, color: chipColor, fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '14px' }}
                    >
                      {displayFn ? displayFn(item) : item}
                      <button type="button" aria-label={`Remove ${item}`} onClick={() => removeFromList(field, item)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: chipColor, display: 'inline-flex', padding: 0 }}>
                        <X size={14} />
                      </button>
                    </span>
                  ))
                )}
              </div>
            );
            return (
              <>
                {/* Target industries - dropdown from Advanced Search options */}
                <div style={{ marginBottom: '18px' }}>
                  <label className="form-label" style={{ fontSize: '13px' }}>Target industries</label>
                  {chipList('target_industries', '#dcfce7', '#15803d')}
                  <div style={{ maxWidth: '460px' }}>
                    <Select value="" onValueChange={(v) => addToList('target_industries', v)}>
                      <SelectTrigger data-testid="target_industries-select">
                        <SelectValue placeholder="Select an industry to add..." />
                      </SelectTrigger>
                      <SelectContent>
                        {targetOptions.industries.map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Target roles - dropdown of Apollo seniorities */}
                <div style={{ marginBottom: '18px' }}>
                  <label className="form-label" style={{ fontSize: '13px' }}>Target roles / titles</label>
                  {chipList('target_titles', '#dbeafe', '#1d4ed8', seniorityLabel)}
                  <div style={{ maxWidth: '460px' }}>
                    <Select value="" onValueChange={(v) => addToList('target_titles', v)}>
                      <SelectTrigger data-testid="target_titles-select">
                        <SelectValue placeholder="Select a seniority/role to add..." />
                      </SelectTrigger>
                      <SelectContent>
                        {targetOptions.seniorities.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Target locations - same autocomplete as Advanced Search */}
                <div style={{ marginBottom: '18px' }}>
                  <label className="form-label" style={{ fontSize: '13px' }}>Target locations</label>
                  {chipList('target_locations', '#fef3c7', '#b45309')}
                  <div style={{ display: 'flex', gap: '8px', maxWidth: '460px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <LocationAutocomplete
                        value={targetInputs.target_locations}
                        onChange={(v) => setTargetInputs({ ...targetInputs, target_locations: v })}
                        placeholder="Search city or state to add..."
                        testId="target_locations-input"
                      />
                    </div>
                    <Button type="button" className="btn btn-secondary" data-testid="target_locations-add-btn"
                      onClick={() => { addToList('target_locations', targetInputs.target_locations); setTargetInputs({ ...targetInputs, target_locations: '' }); }}>
                      Add
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>


        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px', color: '#334155' }}>
            Default Contact Tags
          </h3>
          <p className="form-help" style={{ marginBottom: '12px' }}>
            These tags are automatically applied to every contact synced to your CRM (regular and advanced scans).
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }} data-testid="default-tags-list">
            {(settings.default_tags || []).length === 0 ? (
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>No custom tags added yet.</span>
            ) : (
              (settings.default_tags || []).map((tag) => (
                <span
                  key={tag}
                  data-testid={`default-tag-${tag}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ede9fe', color: '#6d28d9', fontSize: '13px', fontWeight: 600, padding: '4px 10px', borderRadius: '14px' }}
                >
                  {tag}
                  <button
                    type="button"
                    aria-label={`Remove ${tag}`}
                    data-testid={`remove-tag-${tag}`}
                    onClick={() => removeTag(tag)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d28d9', display: 'inline-flex', padding: 0 }}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', maxWidth: '420px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. hot-lead, webinar-2026"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              data-testid="new-tag-input"
            />
            <Button
              type="button"
              onClick={addTag}
              className="btn btn-secondary"
              data-testid="add-tag-btn"
            >
              Add
            </Button>
          </div>
        </div>

        <div style={{ marginTop: '24px' }}>
          <Button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? "Saving..." : "💾 Save Settings"}
          </Button>
        </div>
      </div>

      {/* CRM Integration Section - Manual API Key Entry */}
      <div className="content-card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h2 className="card-title">🔗 CRM Integration</h2>
          {!apiSettings?.managed_by_owner && (
          <Button
            onClick={handleTestApiConnections}
            disabled={testingApi}
            className="btn btn-secondary"
            data-testid="test-api-btn"
          >
            {testingApi ? "Testing..." : "🔌 Test Connection"}
          </Button>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
            {apiSettings?.managed_by_owner
              ? "Your CRM is managed by your account owner. Every scan you run automatically syncs to the team's Revo Digital CRM."
              : "Connect your CRM account to automatically sync contacts and tags. You'll need your API key and Location ID from your CRM."}
          </p>

          {/* Current Status */}
          {apiSettings && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
              <div>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Email Discovery:</span>{' '}
                <strong style={{ color: apiSettings.email_discovery_configured ? '#16a34a' : '#dc2626' }}>
                  {apiSettings.email_discovery_configured ? '✓ Ready' : '✗ Not Available'}
                </strong>
                {!apiSettings.email_discovery_configured && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Contact administrator to enable
                  </div>
                )}
              </div>
              <div>
                <span style={{ fontSize: '13px', color: '#64748b' }}>CRM Connection:</span>{' '}
                <strong style={{ color: apiSettings.ghl_configured ? '#16a34a' : '#dc2626' }}>
                  {apiSettings.ghl_configured ? '✓ Connected' : '✗ Not Connected'}
                </strong>
                {apiSettings.ghl_location_id && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Location: {apiSettings.ghl_location_id}
                  </div>
                )}
                {apiSettings.managed_by_owner && (
                  <div data-testid="crm-managed-by-owner" style={{ fontSize: '11px', color: '#7c3aed', marginTop: '4px', fontWeight: 600 }}>
                    🔒 Managed by your account owner{apiSettings.owner_name ? ` (${apiSettings.owner_name})` : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Test Results */}
          {testResults && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
                Connection Test Results
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                {Object.entries(testResults).map(([service, result]) => (
                  <div
                    key={service}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${result.status === 'success' ? '#bbf7d0' : result.status === 'error' ? '#fecaca' : '#e2e8f0'}`,
                      background: result.status === 'success' ? '#f0fdf4' : result.status === 'error' ? '#fef2f2' : '#f8fafc'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', textTransform: 'capitalize' }}>
                        {service === 'ghl' ? 'CRM' : service.replace('_', ' ')}:
                      </span>
                      <span style={{ 
                        fontSize: '12px', 
                        fontWeight: '600',
                        color: result.status === 'success' ? '#16a34a' : result.status === 'error' ? '#dc2626' : '#64748b'
                      }}>
                        {result.status === 'success' ? '✓ Success' : result.status === 'error' ? '✗ Failed' : 'Not Configured'}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#64748b' }}>
                      {result.message}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CRM Credentials Form (hidden for team members — CRM is owner-managed) */}
          {!apiSettings?.managed_by_owner && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">
                CRM API Key / Access Token
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your CRM API key or access token..."
                value={ghlAccessToken}
                onChange={(e) => setGhlAccessToken(e.target.value)}
                data-testid="ghl-access-token-input"
              />
              <p className="form-help">
                Your CRM API key with contact write permissions. This is typically found in your CRM's Settings → API or Integrations section.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                CRM Location ID
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your CRM location ID..."
                value={ghlLocationId}
                onChange={(e) => setGhlLocationId(e.target.value)}
                data-testid="ghl-location-id-input"
              />
              <p className="form-help">
                Your unique location or account identifier from your CRM. Usually found in Settings → Business Profile or Account Settings.
              </p>
            </div>

            <div>
              <Button
                onClick={handleSaveApiSettings}
                disabled={savingApi || (!ghlAccessToken.trim() && !ghlLocationId.trim())}
                className="btn btn-primary"
                data-testid="save-api-settings-btn"
              >
                {savingApi ? "Saving..." : "💾 Save CRM Settings"}
              </Button>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                Note: Changes take effect immediately. Leave fields blank to keep existing values.
              </p>
            </div>
          </div>
          )}
        </div>

        {!apiSettings?.managed_by_owner && (
        <div style={{ marginTop: '24px', padding: '12px', background: '#eff6ff', borderLeft: '3px solid #2563eb', borderRadius: '4px' }}>
          <strong style={{ color: '#1e40af' }}>💡 Need help finding your credentials?</strong>
          <p style={{ marginTop: '6px', fontSize: '13px', color: '#1e40af' }}>
            Contact your CRM administrator or check your CRM's API documentation for instructions on obtaining your API key and Location ID.
          </p>
        </div>
        )}
      </div>


      {user?.role === 'admin' && <BetaFeaturesPanel />}

      <div className="content-card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <h2 className="card-title">Change Password</h2>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
            Update your account password. You'll need to enter your current password to confirm the change.
          </p>

          <div style={{ display: 'grid', gap: '20px', maxWidth: '500px' }}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="current-password-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                data-testid="confirm-new-password-input"
              />
            </div>

            <div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmNewPassword}
                className="btn btn-primary"
                data-testid="change-password-btn"
              >
                {changingPassword ? "Changing Password..." : "🔒 Change Password"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="content-card" style={{ marginTop: '24px', borderColor: '#fecaca' }}>
        <div className="card-header">
          <h2 className="card-title" style={{ color: '#dc2626' }}>Danger Zone</h2>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
            Once you delete your account, there is no going back. Your account can be recovered by an admin within 31 days, after which it will be permanently deleted.
          </p>

          <button
            onClick={() => setShowDeleteModal(true)}
            data-testid="delete-account-btn"
            style={{
              background: 'white',
              color: '#dc2626',
              border: '1px solid #dc2626',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#dc2626';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#dc2626';
            }}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDeleteModal(false)}
          data-testid="delete-modal-overlay"
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626', marginBottom: '16px' }}>
              Delete Account
            </h2>
            
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
              This action will delete your account. Your account can be recovered by contacting an admin within 31 days. After that, all your data will be permanently removed.
            </p>

            <div style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600', marginBottom: '12px' }}>
                To confirm, type <span style={{ fontFamily: 'monospace', background: '#fee2e2', padding: '2px 6px', borderRadius: '4px' }}>DELETE</span> below:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                data-testid="delete-confirm-input"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                }}
                className="btn btn-secondary"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE" || deleting}
                data-testid="delete-confirm-btn"
                style={{
                  background: deleteConfirmText === "DELETE" ? '#dc2626' : '#e5e7eb',
                  color: deleteConfirmText === "DELETE" ? 'white' : '#9ca3af',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: deleteConfirmText === "DELETE" ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
              >
                {deleting ? "Deleting..." : "Delete My Account"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Settings;
